import { useState, useRef, useEffect, useCallback } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Video, StopCircle } from "lucide-react";
import { MecomRecordingCount } from "../components/MecomRecordingCount";

// ═══════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════
function normalizeCodBip(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes("@id@>") || trimmed.includes("@Id@>") || trimmed.includes("@ID@>")) {
    const match = trimmed.match(/@(?:id|Id|ID)@>@?([^@,]+)@?/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  if (trimmed.includes("^id^Ç^") || trimmed.includes("^Id^Ç^") || trimmed.includes("^ID^Ç^")) {
    const match = trimmed.match(/\^(?:id|Id|ID)\^Ç\^([^^,]+)/);
    if (match && match[1].trim() !== "") return match[1].trim();
  }
  const repeatedMatch = trimmed.match(/^([^,]+),\1(?:,[^,]*)?$/);
  if (repeatedMatch && repeatedMatch[1].trim() !== "") return repeatedMatch[1].trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const id = parsed["id"] ?? parsed["Id"] ?? parsed["ID"];
        if (id !== undefined && id !== null && String(id).trim() !== "") return String(id).trim();
      }
    } catch { /* não é JSON válido */ }
  }
  return trimmed;
}

function playSuccess() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
    setTimeout(() => ctx.close(), 200);
  } catch {}
}

function playError() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(200, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
    setTimeout(() => ctx.close(), 300);
  } catch {}
}

function forceFocus(ref: React.RefObject<HTMLInputElement | null>) {
  ref.current?.focus();
  requestAnimationFrame(() => ref.current?.focus());
  setTimeout(() => ref.current?.focus(), 50);
  setTimeout(() => ref.current?.focus(), 150);
}

async function uploadWithRetry(formData: FormData, maxRetries = 3): Promise<string> {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.url) return result.url;
      throw new Error('Upload retornou sem URL');
    } catch (err) {
      retries++;
      if (retries >= maxRetries) throw err;
      await new Promise(r => setTimeout(r, 1000 * retries));
    }
  }
  throw new Error('Upload falhou após retries');
}

function baixarVideoFalhado(videoBlob: Blob, nomeArquivo: string) {
  const url = URL.createObjectURL(videoBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ═══════════════════════════════════════════════════════════════════
// PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function RecordOrders() {
  const { user } = useGoogleAuth();
  const [pontoOperacionalId, setPontoOperacionalId] = useState<string>(() => localStorage.getItem("mecom_gravacao_pontoId") || "__none__");
  const [codigoBip, setCodigoBip] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [currentBip, setCurrentBip] = useState<string | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recentGravacoes, setRecentGravacoes] = useState<any[]>([]);
  const [recordedInSession, setRecordedInSession] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const recordingStartRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: pontosOperacionais } = trpc.pontosOperacionais.list.useQuery();
  const bipGravacaoMutation = trpc.mecom.bipGravacao.useMutation({
    onSuccess: (_data, variables) => {
      playSuccess();
      toast.success(`Gravação salva: ${variables.codigoBip}`);
      setRecentGravacoes(prev => [{ codigoBip: variables.codigoBip, time: new Date(), duration: variables.duration }, ...prev.slice(0, 99)]);
    },
    onError: (err) => {
      playError();
      toast.error(err.message, { duration: 5000 });
    },
  });

  // Persist ponto operacional
  useEffect(() => { localStorage.setItem("mecom_gravacao_pontoId", pontoOperacionalId); }, [pontoOperacionalId]);

  // Timer for recording duration display
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds(Math.floor((Date.now() - recordingStartRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setRecordingSeconds(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  // Aggressive focus
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("select, button, video, [role='listbox'], [role='option'], [data-radix-select-viewport]")) return;
      forceFocus(inputRef);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  // Initialize camera once (persistent stream)
  const initCamera = useCallback(async () => {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }
      setIsCameraOn(true);
    } catch (err) {
      playError();
      toast.error("Erro ao acessar câmera. Verifique permissões.");
    }
  }, []);

  // Start a new MediaRecorder on the existing stream
  const startNewRecorder = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 2500000,
    });
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    mediaRecorder.start(1000);
    mediaRecorderRef.current = mediaRecorder;
    recordingStartRef.current = Date.now();
    setIsRecording(true);
  }, []);

  // Stop current recorder and upload
  const stopAndSave = useCallback(async (bipCode: string): Promise<{ url: string; duration: number; fileSize: number; startedAt: string }> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        reject('Nenhuma gravação ativa');
        return;
      }
      recorder.onstop = async () => {
        const recordedChunks = [...chunksRef.current];
        chunksRef.current = [];

        const videoBlob = new Blob(recordedChunks, { type: 'video/webm' });
        const duration = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        const fileSize = videoBlob.size;
        const startedAt = new Date(recordingStartRef.current).toISOString();
        const nomeArquivo = `mecom_${bipCode}_${Date.now()}.webm`;

        const formData = new FormData();
        formData.append('file', videoBlob, nomeArquivo);

        try {
          setIsUploading(true);
          const url = await uploadWithRetry(formData, 3);
          setIsUploading(false);
          resolve({ url, duration, fileSize, startedAt });
        } catch (err) {
          setIsUploading(false);
          baixarVideoFalhado(videoBlob, nomeArquivo);
          reject('Upload falhou após 3 tentativas. O vídeo foi baixado neste computador.');
        }
      };
      recorder.stop();
      setIsRecording(false);
    });
  }, []);

  // Stop camera completely
  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    setIsCameraOn(false);
    setIsRecording(false);
    setCurrentBip(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const handleBip = async () => {
    if (!codigoBip.trim()) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Selecione um ponto operacional antes de gravar.", { duration: 5000 });
      return;
    }
    const normalized = normalizeCodBip(codigoBip);
    setCodigoBip("");

    // Duplicate check (session level)
    if (recordedInSession.has(normalized)) {
      playError();
      toast.error(`Código ${normalized} já foi gravado nesta sessão!`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    // Validação prévia: verificar se pedido existe antes de gravar
    try {
      const checkRes = await fetch(`/api/trpc/mecom.checkPedido?input=${encodeURIComponent(JSON.stringify({ json: { codigoBip: normalized } }))}`, { credentials: 'include' });
      const checkJson = await checkRes.json();
      const check = checkJson?.result?.data?.json;
      if (!check.exists) {
        playError();
        toast.error(`Pedido não encontrado: ${normalized}. Realize a entrada primeiro.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'gravado') {
        playError();
        toast.error(`Pedido ${normalized} já foi gravado.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'expedido') {
        playError();
        toast.error(`Pedido ${normalized} já foi expedido. Não é possível gravar.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
      if (check.status === 'devolvido') {
        playError();
        toast.error(`Pedido ${normalized} já foi devolvido. Não é possível gravar.`, { duration: 5000 });
        forceFocus(inputRef);
        return;
      }
    } catch (err: any) {
      playError();
      toast.error(`Erro ao verificar pedido: ${err.message}`, { duration: 5000 });
      forceFocus(inputRef);
      return;
    }

    // If already recording, stop previous and save
    if (isRecording && currentBip) {
      try {
        const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
        bipGravacaoMutation.mutate({
          codigoBip: currentBip,
          pontoOperacionalId: Number(pontoOperacionalId),
          videoUrl: url,
          duration,
          fileSize,
          startedAt,
        });
        setRecordedInSession(prev => new Set(prev).add(currentBip));
      } catch (err) {
        playError();
        toast.error(`Erro ao salvar gravação de ${currentBip}: ${err}`);
      }
    }

    // Initialize camera if not yet
    if (!streamRef.current) {
      await initCamera();
    }

    // Start new recording
    setCurrentBip(normalized);
    startNewRecorder();
    forceFocus(inputRef);
  };

  const handleStop = async () => {
    if (!isRecording || !currentBip) return;
    if (pontoOperacionalId === "__none__") {
      playError();
      toast.error("Ponto operacional não selecionado.");
      return;
    }
    try {
      const { url, duration, fileSize, startedAt } = await stopAndSave(currentBip);
      bipGravacaoMutation.mutate({
        codigoBip: currentBip,
        pontoOperacionalId: Number(pontoOperacionalId),
        videoUrl: url,
        duration,
        fileSize,
        startedAt,
      });
      setRecordedInSession(prev => new Set(prev).add(currentBip));
    } catch (err) {
      playError();
      toast.error(`Erro ao salvar: ${err}`);
    }
    setCurrentBip(null);
    forceFocus(inputRef);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Gravar Pedidos</h1>

      <div className="space-y-4">
        <div className="flex gap-3 items-center">
          <span className="font-medium">Ponto Operacional:</span>
          <Select value={pontoOperacionalId} onValueChange={setPontoOperacionalId}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Selecione...</SelectItem>
              {(pontosOperacionais as any[] || []).map((p: any) => (
                <SelectItem key={p.id} value={String(p.id)}>{p.tipo} - #{p.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isCameraOn && (
            <Button variant="outline" onClick={initCamera}>Ligar Câmera</Button>
          )}
          {isCameraOn && !isRecording && (
            <Button variant="outline" onClick={stopCamera}>Desligar Câmera</Button>
          )}
        </div>

        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={codigoBip}
            onChange={(e) => setCodigoBip(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBip()}
            placeholder="Bipe o código para iniciar gravação..."
            className="flex-1 font-mono"
            autoFocus
          />
          <Button onClick={handleBip} disabled={isUploading}>
            <Video className="w-4 h-4 mr-1" /> Bipar
          </Button>
          {isRecording && (
            <Button onClick={handleStop} variant="destructive" disabled={isUploading}>
              <StopCircle className="w-4 h-4 mr-1" /> Parar Gravação
            </Button>
          )}
        </div>

        {isRecording && currentBip && (
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">Gravando: <span className="font-mono">{currentBip}</span></span>
            <span className="text-sm font-mono text-red-600 ml-auto">{formatTime(recordingSeconds)}</span>
          </div>
        )}

        <MecomRecordingCount isRecording={isRecording} codigoBip={currentBip} videoRef={videoPreviewRef} />

        {isUploading && (
          <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">Enviando vídeo...</span>
          </div>
        )}

        <video ref={videoPreviewRef} className="w-full max-w-lg rounded border" muted playsInline style={{ display: isCameraOn ? 'block' : 'none' }} />

        {recentGravacoes.length > 0 && (
          <div className="border rounded-lg p-3">
            <h4 className="font-semibold mb-2">Gravações desta sessão ({recentGravacoes.length})</h4>
            <div className="max-h-[200px] overflow-auto space-y-1">
              {recentGravacoes.map((g, i) => (
                <div key={i} className="flex justify-between text-sm font-mono bg-yellow-50 p-1 rounded">
                  <span>{g.codigoBip}</span>
                  <span className="text-muted-foreground">{g.duration ? `${g.duration}s` : ''} — {g.time.toLocaleTimeString('pt-BR')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
